import type { ConfigMotor } from "./config.ts";
import { Maturidade } from "./maturidade.ts";
import type { ChaveSelo, Conformidade, Eixo, ItemAvaliado, Momento, ResultadoImgq, Selo } from "./types.ts";

/** Maturidade de um item no momento, ou `null` se não respondido. */
function maturidadeDe(item: ItemAvaliado, momento: Momento): Maturidade | null {
  const valor = item[momento];
  if (valor === null || valor === undefined) {
    return null;
  }
  return Maturidade.de(valor);
}

function scoreDe(item: ItemAvaliado, momento: Momento, config: ConfigMotor): number | null {
  const mat = maturidadeDe(item, momento);
  if (mat === null) {
    return null;
  }
  return mat.score(config);
}

/** Score médio ponderado pelo peso dos itens (curva ONA). `null` se vazio. */
export function setorScore(
  items: readonly ItemAvaliado[],
  momento: Momento,
  config: ConfigMotor,
): number | null {
  let num = 0;
  let den = 0;
  for (const item of items) {
    const score = scoreDe(item, momento, config);
    if (score === null) {
      continue;
    }
    num += score * item.peso;
    den += item.peso;
  }
  return den > 0 ? num / den : null;
}

/** Subíndice de um eixo (IMGO/IMQA/IMGR). */
export function subindice(
  items: readonly ItemAvaliado[],
  eixo: Eixo,
  momento: Momento,
  config: ConfigMotor,
): number | null {
  return setorScore(
    items.filter((it) => it.eixo === eixo),
    momento,
    config,
  );
}

function ehCore(item: ItemAvaliado): boolean {
  return item.peso === 3;
}

/** Conta itens CORE cuja maturidade (não-NA) está no nível dado (0 ou 1). */
export function contarCore(items: readonly ItemAvaliado[], nivel: 0 | 1, momento: Momento): number {
  return items.filter((it) => {
    if (!ehCore(it)) {
      return false;
    }
    const mat = maturidadeDe(it, momento);
    return mat !== null && !mat.ehNA && mat.nivel === String(nivel);
  }).length;
}

/** Conta itens por conformidade derivada. */
export function contarConf(items: readonly ItemAvaliado[], conf: Conformidade, momento: Momento): number {
  return items.filter((it) => maturidadeDe(it, momento)?.conformidade === conf).length;
}

/** Conta itens marcados como Não Aplicável. */
export function contarNA(items: readonly ItemAvaliado[], momento: Momento): number {
  return items.filter((it) => maturidadeDe(it, momento)?.ehNA === true).length;
}

const EIXOS: readonly Eixo[] = ["IMGO", "IMQA", "IMGR"];

/**
 * Nº de itens por eixo p/ a ponderação: override fixo da config, ou — quando
 * `null` — a contagem dos itens que CONTRIBUEM com nota (score não-nulo) no
 * momento. Contar só os pontuados (e não os NA/não respondidos) é o que faz
 * "setor fora de escopo" pesar IGUAL a "itens marcados NA" no IMGQ bruto
 * (teste-ouro doc 17b §7): em ambos o item não entra no numerador nem no peso.
 */
function pesosEixo(
  items: readonly ItemAvaliado[],
  momento: Momento,
  config: ConfigMotor,
): Record<Eixo, number> {
  if (config.pesoEixos !== null) {
    return { ...config.pesoEixos };
  }
  const contagem: Record<Eixo, number> = { IMGO: 0, IMQA: 0, IMGR: 0 };
  for (const item of items) {
    if (scoreDe(item, momento, config) !== null) {
      contagem[item.eixo] += 1;
    }
  }
  return contagem;
}

/**
 * IMGQ bruto = média dos subíndices PRESENTES (não-`null`), ponderada pelo nº de
 * itens do eixo (doc 11). Eixo sem itens aplicáveis (`null`) fica fora — não é
 * tratado como zero (vacuidade ≠ nota baixa). `0` só quando nada foi pontuado.
 */
function bruto(
  subs: Readonly<Record<Eixo, number | null>>,
  items: readonly ItemAvaliado[],
  momento: Momento,
  config: ConfigMotor,
): number {
  const presentes = EIXOS.filter((e) => subs[e] !== null);
  if (presentes.length === 0) {
    return 0;
  }
  if (config.ponderacao === "simples") {
    return presentes.reduce((s, e) => s + (subs[e] as number), 0) / presentes.length;
  }
  const peso = pesosEixo(items, momento, config);
  let num = 0;
  let den = 0;
  for (const e of presentes) {
    num += (subs[e] as number) * peso[e];
    den += peso[e];
  }
  return den > 0 ? num / den : 0;
}

/** Ajuste final assimétrico (só na FINAL): nunca promove de faixa de selo. */
function ajustar(efetivo: number, qaci: number, ims: number, config: ConfigMotor): number {
  const soma = efetivo + qaci * config.qaciFaixa + (Math.max(0, Math.min(1, ims)) - 0.5) * config.imsFaixa;
  if (efetivo < config.seloBronze) {
    return Math.max(0, Math.min(soma, config.seloBronze - 0.001));
  }
  if (efetivo < config.seloPrata) {
    return Math.min(soma, config.seloPrata - 0.001);
  }
  if (efetivo < config.seloOuro) {
    return Math.min(soma, config.seloOuro - 0.001);
  }
  return soma;
}

/**
 * Cadeia completa item → subíndices → IMGQ bruto → efetivo (exclusão CORE) →
 * ajustado (QACI/IMS, só na final). `qaci`/`ims` só são aplicados quando
 * `momento === "mf"` e ambos informados.
 */
export function imgq(
  items: readonly ItemAvaliado[],
  momento: Momento,
  config: ConfigMotor,
  qaci?: number,
  ims?: number,
): ResultadoImgq {
  const imgo = subindice(items, "IMGO", momento, config);
  const imqa = subindice(items, "IMQA", momento, config);
  const imgr = subindice(items, "IMGR", momento, config);
  const valorBruto = bruto({ IMGO: imgo, IMQA: imqa, IMGR: imgr }, items, momento, config);

  const core0 = contarCore(items, 0, momento);
  const core1 = contarCore(items, 1, momento);
  // Eixo `null` (sem itens aplicáveis) NÃO trava por vacuidade — só dispara a
  // meta quem tem subíndice real abaixo de 0,46 (doc 17b §3.4).
  const abaixoMeta = (s: number | null) => s !== null && s < config.metaSubindice;
  const travado =
    config.exclusaoAtiva &&
    (core0 > 0 || core1 > 0 || abaixoMeta(imgo) || abaixoMeta(imqa) || abaixoMeta(imgr));
  const efetivo = travado ? Math.min(valorBruto, config.tetoExclusao) : valorBruto;

  const ajustado =
    momento === "mf" && qaci !== undefined && ims !== undefined
      ? ajustar(efetivo, qaci, ims, config)
      : efetivo;

  return { imgo, imqa, imgr, bruto: valorBruto, efetivo, ajustado, core0, core1, travado };
}

const SELOS: Readonly<Record<Exclude<ChaveSelo, "pend">, Omit<Selo, "chave">>> = {
  ouro: { rotulo: "OURO", nivel: "Nível 3 · Excelência", romano: "III" },
  prata: { rotulo: "PRATA", nivel: "Nível 2 · Estruturado", romano: "II" },
  bronze: { rotulo: "BRONZE", nivel: "Nível 1 · Inicial", romano: "I" },
  dev: { rotulo: "EM DESENVOLVIMENTO", nivel: "Nível 0 · Diagnóstico", romano: "0" },
};

/** Selo QualiEMSERH a partir do IMGQ (ajustado na final, efetivo na inicial). */
export function selo(valor: number | null, config: ConfigMotor): Selo {
  if (valor === null) {
    return { chave: "pend", rotulo: "Aguardando", nivel: "", romano: "—" };
  }
  if (valor >= config.seloOuro) {
    return { chave: "ouro", ...SELOS.ouro };
  }
  if (valor >= config.seloPrata) {
    return { chave: "prata", ...SELOS.prata };
  }
  if (valor >= config.seloBronze) {
    return { chave: "bronze", ...SELOS.bronze };
  }
  return { chave: "dev", ...SELOS.dev };
}

/** QACI final = soma ponderada das médias dos 3 componentes (C1=0,5 C2=0,3 C3=0,2). */
export function qaciFinal(c1: number, c2: number, c3: number, config: ConfigMotor): number {
  return c1 * config.qaciC1 + c2 * config.qaciC2 + c3 * config.qaciC3;
}
